import { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { helpers } from "@shared";
import classNames from "classnames";
import { useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { useAllowedPaymentTypes } from "@hooks";
import { ChevronLeftIcon } from "@icons";
import { PaymentType } from "./shared/enums";
import { PaymentMethodBadge } from "./payment-method-badge";
import { Button, IconButton } from "@common";

interface PaymentMethodInfo {
    accounts: Record<string, string>;
    sourceName: string | null;
    last4: string;
    idPaymentType: number;
    idPaymentMethod: number;
    accountType: string | null;
    isValid: boolean;
}

type PaymentMethodsMap = Record<string, PaymentMethodInfo>;

const ExistingPaymentMethods = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const {
        state: {
            accounts = null,
            account,
            hasMultipleAccounts,
            setupAccounts
        }
    } = location;
    const { idAccount, residentName } = account;
    const [selectedPM, setSelectedPM] = useState<number | null>(null);
    const { setSpinnerShow } = useSpinnerContext();
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsMap | null>(null);
    const { allowedPaymentTypes } = useAllowedPaymentTypes();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        (async () => {
            setSpinnerShow(true);
            try {
                const response = await sunboundPayProxy.getPayorPaymentMethods({ isActive: true, withAccountAssignments: true });
                const { idAccount: currentAccount } = account;
                let current: number | null = null;
                const pmMap: PaymentMethodsMap = {};
                response.forEach(pm => {
                    const { idPaymentMethod, accountPaymentMethods, sourceName, last4, idPaymentType, accountType } = pm;
                    const acc: Record<string, string> = {};
                    const isValid = allowedPaymentTypes.includes(idPaymentType);
                    accountPaymentMethods.forEach(apm => {
                        const { isDefault, accountPayor: { account: { idAccount, residentName } } } = apm;
                        if (idAccount === currentAccount)
                            current = idPaymentMethod;
                        if (isDefault)
                            acc[idAccount] = residentName;
                    });
                    pmMap[idPaymentMethod] = {
                        accounts: acc,
                        sourceName,
                        last4,
                        idPaymentType,
                        idPaymentMethod,
                        accountType,
                        isValid
                    };
                });

                setPaymentMethods(pmMap);
                setSelectedPM(current);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);

        })();
    }, [accounts, allowedPaymentTypes]);

    const register = async () => {
        if (!selectedPM || !paymentMethods) return;

        setSpinnerShow(true);
        try {

            await sunboundPayProxy.upsertAccountDefaultPaymentMethod(idAccount, selectedPM);

            if (hasMultipleAccounts) {
                if (setupAccounts.length > 0) {
                    const [nextAccount, ...rest] = setupAccounts;
                    helpers.navigateTo(`.`, location, navigate, undefined, { account: nextAccount, setupAccounts: [...rest] });
                } else {
                    helpers.navigateNext(location, navigate, { missingAccounts: false });
                }
            } else {
                const defaultPaymentMethod = { ...paymentMethods[selectedPM] };
                helpers.navigateNext(location, navigate, { hasDefaultPaymentMethod: true }, { defaultPaymentMethod });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSpinnerShow(false);
        }

    };

    const addNew = () => {
        if (hasMultipleAccounts)
            helpers.navigateNext(location, navigate);
        else
            helpers.navigateNext(location, navigate, { hasDefaultPaymentMethod: false });
    };

    const goBack = () => {
        navigate(-1);
    };

    const getPaymentMethodDisplayName = (pm: PaymentMethodInfo) => {
        const { idPaymentType, sourceName } = pm;
        return Number(idPaymentType) === PaymentType.ACH ? `ACH` : sourceName || `Card`;
    };

    const getAssignedResidentName = (accounts: Record<string, string>) => {
        const keys = Object.keys(accounts);
        if (!accounts || keys.length === 0) return null;
        // Get the first assigned resident name
        const firstKey = keys[0]; // For TS compiler; firstKey is always defined
        return firstKey !== undefined ? accounts[firstKey] : null;
    };

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <div className="flex items-center gap-2">
                    <IconButton
                        variant="tertiary"
                        size="sm"
                        className="flex-shrink-0 md:btn-icon-lg"
                        onClick={goBack}
                        aria-label="Go back"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                        Add Payment Method
                    </h1>
                </div>
            </div>

            {/* Context and Instructions */}
            <div className="mb-8">
                <p className="text-base leading-6 font-normal text-neutral-950 tracking-[0.16px] mb-2">
                    Existing Payment Methods for {residentName}
                </p>
                <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px]">
                    Select which payment method you’d like to use, or add a new one.
                </p>
            </div>

            {/* Payment Methods List */}
            <div className="flex flex-col gap-4 mb-8" role="radiogroup" aria-label="Payment methods">
                {paymentMethods && Object.values(paymentMethods).map((pm) => {
                    const { idPaymentMethod, sourceName, last4, accounts, isValid, idPaymentType } = pm;
                    const isSelected = selectedPM === idPaymentMethod;
                    const isDisabled = !isValid;
                    const assignedTo = getAssignedResidentName(accounts);

                    return (
                        <label
                            key={idPaymentMethod}
                            className={classNames(
                                `flex items-center gap-4 px-6 py-4 rounded-xl border bg-white text-left transition-colors`,
                                isDisabled ? `opacity-50 cursor-not-allowed bg-neutral-50 border-neutral-100` :
                                isSelected ? `border-neutral-300 cursor-pointer` : `border-neutral-200 hover:border-neutral-300 cursor-pointer`,
                                `focus-within:outline-focus-ring`
                            )}
                        >
                            {/* Hidden Radio Input */}
                            <input
                                type="radio"
                                name="payment-method"
                                value={idPaymentMethod}
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => !isDisabled && setSelectedPM(idPaymentMethod)}
                                className="sr-only"
                            />

                            {/* Visual Radio Button */}
                            <div className="flex-shrink-0">
                                <div className={classNames(
                                    `w-5 h-5 rounded-xl border flex items-center justify-center transition-colors bg-white`,
                                    isSelected ? `border-neutral-950` : `border-neutral-200`
                                )}>
                                    {isSelected && (
                                        <div className="w-3.5 h-3.5 rounded-full bg-neutral-950" />
                                    )}
                                </div>
                            </div>

                            {/* Payment Method Info */}
                            <div className="flex-1 min-w-0 flex flex-col gap-1">
                                <div className="flex items-center gap-4">
                                    <span className="text-base leading-6 font-normal text-neutral-950 tracking-[0.16px]">
                                        {getPaymentMethodDisplayName(pm)}
                                    </span>
                                    <div className="inline-flex items-center gap-1">
                                        <PaymentMethodBadge
                                            paymentType={Number(idPaymentType)}
                                            sourceName={sourceName}
                                        />
                                        <span className="text-sm leading-5 font-normal text-neutral-700 tracking-[0.14px]">
                                            {last4}
                                        </span>
                                    </div>
                                </div>
                                {assignedTo && (
                                    <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px]">
                                        Assigned to {assignedTo}
                                    </p>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col md:flex-row-reverse gap-4">
                <Button
                    variant="primary"
                    size="lg"
                    className="w-full md:flex-1"
                    onClick={register}
                    disabled={!selectedPM || !paymentMethods || !paymentMethods[selectedPM]?.isValid}
                >
                    Continue
                </Button>
                <Button
                    variant="secondary"
                    size="lg"
                    className="w-full md:flex-1"
                    onClick={addNew}
                >
                    New Payment Method
                </Button>
            </div>
        </div>
    );
};

export default ExistingPaymentMethods;
